import { useCallback, useEffect, useRef } from 'react'

import { useReportCrossClientViewMutation } from '../../../store/crossClient/crossClient'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  addCrossClientClients,
  addCrossClientFetchedClientIds,
  removeCrossClientFetchedClientIds,
  selectCrossClientClients,
  selectCrossClientFetchedClientIds,
  selectCrossClientResultsExecution,
  setCrossClientClientsLoading,
} from '../../../store/crossClient/crossClientResults'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { useLazyGetAllUsersQuery } from '../../../store/user/user'
import { selectAccountId } from '../../../store/auth/authSlice'
import { type User } from '../../../models/User'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { type CrossClientQueryResult } from '../../../models/CrossClient'

const FETCH_CHUNK_SIZE = 500

export const useFetchMissingCrossClientClients = () => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const fetchedClientIds = useAppSelector(selectCrossClientFetchedClientIds)
  const [triggerGetAllUsers] = useLazyGetAllUsersQuery()

  return useCallback(
    async (clientIds: string[]): Promise<Record<string, User>> => {
      const missingIds = clientIds.filter((id) => !fetchedClientIds[id])
      if (!missingIds.length) {
        return {}
      }

      dispatch(setCrossClientClientsLoading(true))
      dispatch(addCrossClientFetchedClientIds(missingIds))

      const fetchedClients: Record<string, User> = {}
      try {
        const chunks = Array.from({ length: Math.ceil(missingIds.length / FETCH_CHUNK_SIZE) }, (_, index) =>
          missingIds.slice(index * FETCH_CHUNK_SIZE, (index + 1) * FETCH_CHUNK_SIZE),
        )
        for (const chunk of chunks) {
          const response = await triggerGetAllUsers({ accountId, ids: chunk }).unwrap()
          if (response?.data) {
            const clients = response.data.filter((contact) => contact.roles.some((role) => role.name === 'Client'))
            dispatch(addCrossClientClients(clients))
            for (const client of clients) {
              fetchedClients[client.id] = client
            }
          }
        }
        return fetchedClients
      } catch (e) {
        dispatch(removeCrossClientFetchedClientIds(missingIds))
        throw e
      } finally {
        dispatch(setCrossClientClientsLoading(false))
      }
    },
    [accountId, dispatch, fetchedClientIds, triggerGetAllUsers],
  )
}

export const useExportCrossClientResults = (resolveResults: () => Promise<CrossClientQueryResult[]>) => {
  const { tracked } = useTracking()

  const execution = useAppSelector(selectCrossClientResultsExecution)
  const clients = useAppSelector(selectCrossClientClients)

  const fetchMissingClients = useFetchMissingCrossClientClients()

  const exportCSV = tracked(() => _exportCSV(), {
    eventName: TrackEvent.X_CLIENT_RESULT_EXPORTED,
    eventDetails: {
      executionId: execution?.id,
    },
  })

  const _exportCSV = async () => {
    const results = await resolveResults()
    if (!results.length) {
      return
    }

    const missingClients = await fetchMissingClients(results.map((result) => result.clientId))

    const allClients = { ...clients, ...missingClients }

    const csvRows = [
      ['Client', 'Email', 'Phone Number'],
      ...results.map((result) => {
        const client = allClients[result.clientId]
        const clientName = client ? getUserDisplayName(client) : 'Unknown'
        const clientEmail = client ? client.email : 'Unknown'
        const clientPhoneNumber = client && client.phoneNumber ? client.phoneNumber : ''

        return [clientName, clientEmail, clientPhoneNumber]
      }),
    ]

    const csvContent = csvRows.map((row) => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })

    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.href = url
    link.download = `CrossClientResults_${execution?.id}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  return { exportCSV }
}

export const useReportCrossClientResultsView = (executionId: string): void => {
  const accountId = useAppSelector(selectAccountId)
  const execution = useAppSelector(selectCrossClientResultsExecution)

  const [reportCrossClientView] = useReportCrossClientViewMutation()

  const handledExecutionRef = useRef<string | null>(null)

  useEffect(() => {
    if (!accountId || handledExecutionRef.current === executionId || execution?.id !== executionId) {
      return
    }
    if (execution.usageReportedAt) {
      handledExecutionRef.current = executionId
      return
    }
    handledExecutionRef.current = executionId

    const reportView = async () => {
      try {
        await reportCrossClientView({ accountId, executionId }).unwrap()
      } catch {
        if (handledExecutionRef.current === executionId) {
          handledExecutionRef.current = null
        }
      }
    }

    void reportView()
  }, [accountId, executionId, execution, reportCrossClientView])
}
